import { useAbility } from "@/casl-provider";
import type { OpenPosition } from "@/common/api/open-position/use-open-position-search.query";
import type { RowActionHelpers } from "@/components/data-table/predefined/_components/base-predefined-table.types";
import type { DataTableRow } from "@/components/data-table/types";
import { RowAction } from "@/components/ui/table-row-actions";
import { RefreshCwIcon } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSyncOpenPosition } from "../../_hooks/use-sync-open-position";
import { useSyncOpenPositionToV1 } from "../../_hooks/use-sync-open-position-to-v1";

export function useOpenPositionSyncActions(): {
  buildRowActions: (row: DataTableRow<OpenPosition>, helpers: RowActionHelpers) => RowAction[];
} {
  const { t } = useTranslation(["fia"]);
  const ability = useAbility();
  const canSyncOpenPositions = ability.can("read", "OpenPosition");

  const { syncOpenPosition, isSyncing } = useSyncOpenPosition();
  const { syncOpenPositionToV1, isSyncingToV1 } = useSyncOpenPositionToV1();

  const buildRowActions = useCallback(
    (row: DataTableRow<OpenPosition>): RowAction[] => {
      if (!canSyncOpenPositions) return [];

      return [
        {
          label: t("fia:SyncOpenPosition"),
          icon: <RefreshCwIcon className="h-4 w-4" />,
          disabled: isSyncing,
          onClick: () => void syncOpenPosition(row.original.uuid),
        },
        {
          label: t("fia:SyncOpenPositionToV1"),
          icon: <RefreshCwIcon className="h-4 w-4" />,
          disabled: isSyncingToV1,
          onClick: () => void syncOpenPositionToV1(row.original.uuid),
        },
      ];
    },
    [canSyncOpenPositions, isSyncing, isSyncingToV1, syncOpenPosition, syncOpenPositionToV1, t],
  );

  return { buildRowActions };
}
